/**
 * Custom hook for managing PDF text search
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import type { TextMatch } from '../types/pdf-viewer.types';

export interface UsePDFSearchProps {
  highlightText?: string;
  isOpen: boolean;
  pageNumber: number;
  setPageNumber: (page: number | ((prev: number) => number)) => void;
  onPageChange?: (page: number) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
}

export interface UsePDFSearchReturn {
  searchText: string;
  searchInput: string;
  showSearchBar: boolean;
  textMatches: TextMatch[];
  currentMatchIndex: number;
  setSearchInput: (input: string) => void;
  setShowSearchBar: (show: boolean) => void;
  handleSearch: (query: string) => void;
  handleSearchSubmit: (e: React.FormEvent) => void;
  clearSearch: () => void;
  goToPreviousMatch: () => void;
  goToNextMatch: () => void;
  textRenderer: (textItem: any) => React.ReactNode;
}

/**
 * Manages PDF text search state and operations
 */
export function usePDFSearch({
  highlightText,
  isOpen,
  pageNumber,
  setPageNumber,
  onPageChange,
  searchInputRef,
}: UsePDFSearchProps): UsePDFSearchReturn {
  const [searchText, setSearchText] = useState<string>(highlightText || '');
  const [searchInput, setSearchInput] = useState<string>('');
  const [showSearchBar, setShowSearchBar] = useState<boolean>(false);
  const [textMatches, setTextMatches] = useState<TextMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(0);

  // Set initial search text when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchText(highlightText || '');
      setCurrentMatchIndex(0);
    }
  }, [isOpen, highlightText]);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    setSearchInput(query);
    if (query.trim().length >= 2) {
      setSearchText(query);
      setCurrentMatchIndex(0);
      // Uses textRenderer for highlighting
    } else {
      setSearchText('');
      setTextMatches([]);
    }
  }, []);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSearch(searchInput);
    },
    [searchInput, handleSearch]
  );

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchText('');
    setTextMatches([]);
    setShowSearchBar(false);
  }, []);

  // Navigate between text matches
  const goToPreviousMatch = useCallback(() => {
    if (textMatches.length === 0) return;
    const newIndex = currentMatchIndex === 0 ? textMatches.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(newIndex);
    const match = textMatches[newIndex];
    setPageNumber(match.pageNumber);
    onPageChange?.(match.pageNumber);
  }, [textMatches, currentMatchIndex, setPageNumber, onPageChange]);

  const goToNextMatch = useCallback(() => {
    if (textMatches.length === 0) return;
    const newIndex = (currentMatchIndex + 1) % textMatches.length;
    setCurrentMatchIndex(newIndex);
    const match = textMatches[newIndex];
    setPageNumber(match.pageNumber);
    onPageChange?.(match.pageNumber);
  }, [textMatches, currentMatchIndex, setPageNumber, onPageChange]);

  // Custom text renderer to highlight search text
  const textRenderer = useCallback(
    (textItem: any) => {
      if (!searchText || searchText.trim().length === 0) return textItem.str;

      const text = textItem.str;
      const searchLower = searchText.trim().toLowerCase();
      const textLower = text.toLowerCase();

      // Only highlight if there's a meaningful match (at least 10 characters to avoid highlighting table cells and numbers)
      if (!textLower.includes(searchLower) || searchLower.length < 10) {
        return text;
      }

      // Find all occurrences
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let index = textLower.indexOf(searchLower);

      while (index !== -1) {
        // Add text before match
        if (index > lastIndex) {
          parts.push(text.substring(lastIndex, index));
        }

        // Add highlighted match
        const matchText = text.substring(index, index + searchText.length);
        parts.push(
          <mark
            key={`${textItem.itemIndex}-${index}`}
            style={{
              backgroundColor: `rgb(var(--nb-warning-rgb) / 0.5)`,
              padding: '2px 0',
              borderRadius: '2px',
            }}
          >
            {matchText}
          </mark>
        );

        lastIndex = index + searchText.length;
        index = textLower.indexOf(searchLower, lastIndex);
      }

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return <>{parts}</>;
    },
    [searchText]
  );

  return {
    searchText,
    searchInput,
    showSearchBar,
    textMatches,
    currentMatchIndex,
    setSearchInput,
    setShowSearchBar,
    handleSearch,
    handleSearchSubmit,
    clearSearch,
    goToPreviousMatch,
    goToNextMatch,
    textRenderer,
  };
}
